import { Env } from "@/env";
import { useUser } from "@features/user/use-user";
import { SparklesIcon } from "@heroicons/react/24/outline";
import { Button } from "@radix-ui/themes";
import { Check } from "lucide-react";
import posthog from "posthog-js";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Floating3DImage } from "./floating-3d-image";
import { ResponsiveModal } from "./responsive-modal";

interface QualityChoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onContinueStandard: () => void;
  onAddMorePictures: () => void;
  onUpgrade: () => void;
  hideCloseButton?: boolean;
}

export const QualityChoiceModal = ({
  isOpen,
  onClose,
  onContinueStandard,
  onAddMorePictures,
  onUpgrade,
  hideCloseButton = false,
}: QualityChoiceModalProps) => {
  const { t } = useTranslation();
  const { user, credits, refresh } = useUser();
  const navigate = useNavigate();
  const hasEnoughCredits = credits >= 400;
  const [paymentType, setPaymentType] = useState<"subscribe" | "once">(
    "subscribe",
  );

  useEffect(() => {
    if (isOpen) {
      refresh();
    }
  }, [isOpen, refresh]);

  useEffect(() => {
    if (isOpen)
      posthog.capture("click_upgrade_needed", { type: "quality_choice" });
  }, [isOpen]);

  const handleDirectCheckout = () => {
    const plan = paymentType === "subscribe" ? "essential" : "single_3000";
    const interval = paymentType === "subscribe" ? "month" : "3000";
    const checkoutUrl = `${Env.server}/api/billing/checkout?plan=${plan}&interval=${interval}&id=${user?.data?.id}&lang=${user?.data?.lang}`;

    posthog.capture("quality_modal_direct_checkout", {
      payment_type: paymentType,
      plan,
      interval,
    });

    window.location.href = checkoutUrl;
  };

  const handleViewAllPlans = () => {
    posthog.capture("quality_modal_view_all_plans");
    navigate("/billing");
    onClose();
  };

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={onClose}
      title={t("quality_modal.title")}
      height="auto"
      maxHeight="80vh"
      className="!max-w-4xl"
      contentClassName="!p-0"
      hideCloseButton={hideCloseButton}
    >
      <div className="flex flex-col-reverse md:grid md:grid-cols-2 gap-0 md:m-0 -m-4">
        {/* Left Column - Standard Quality */}
        <div className="md:border-r border-slate-200 dark:border-slate-700 flex flex-col">
          <div className="flex-1 p-8 space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-2 text-slate-900 dark:text-white">
                {t("quality_modal.standard_section.title")}
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {t("quality_modal.standard_section.description")}
              </p>
            </div>

            {/* Placeholder for demo image */}
            <div
              className="m-auto aspect-square bg-slate-200 dark:bg-slate-700 rounded-lg flex items-center justify-center border-2 border-black"
              style={{
                backgroundImage: "url('/images/model_low_low.jpg')",
                backgroundSize: "cover",
                backgroundPosition: "center",
                maxHeight: "30vh",
              }}
            ></div>

            <div className="space-y-2">
              <div className="flex items-start gap-2">
                <span className="text-slate-600 dark:text-slate-400">•</span>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {t("quality_modal.standard_section.feature_1")}
                </p>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-slate-600 dark:text-slate-400">•</span>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {t("quality_modal.standard_section.feature_2")}
                </p>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-slate-600 dark:text-slate-400">•</span>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {t("quality_modal.standard_section.feature_3")}
                </p>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-slate-600 dark:text-slate-400">•</span>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {t("quality_modal.standard_section.feature_4")}
                </p>
              </div>
            </div>
          </div>

          <div className="p-8 pt-0">
            <Button
              size="3"
              variant="ghost"
              className="w-full"
              onClick={() => {
                onContinueStandard();
                onClose();
              }}
            >
              {t("quality_modal.standard_section.button")}
            </Button>
          </div>
        </div>

        {/* Right Column - High Quality */}
        <div className="bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 p-8 text-white flex flex-col">
          <div className="flex-1 space-y-6">
            <div className="flex-1 space-y-2">
              <div className="flex items-center gap-2 mb-2">
                <SparklesIcon className="w-6 h-6" />
                <h3 className="text-lg font-semibold">
                  {t("quality_modal.premium_section.title")}
                </h3>
              </div>
              <p className="text-sm text-white/90">
                {t("quality_modal.premium_section.description")}
              </p>
            </div>

            {/* Placeholder for demo image */}
            <Floating3DImage
              imageUrl="/images/model_high.png"
              highResImageUrl="/images/model_high_uhd.webp"
              className="m-auto aspect-square bg-white/10 backdrop-blur-sm rounded-lg flex items-center justify-center border-2 border-white max-h-[30vh]"
              maxRotation={15}
              effectRadius={400}
            />

            <div className="space-y-2">
              <div className="flex items-start gap-2">
                <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="w-3 h-3" />
                </div>
                <p className="text-sm text-white/90">
                  {t("quality_modal.premium_section.feature_1")}
                </p>
              </div>
              <div className="flex items-start gap-2">
                <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="w-3 h-3" />
                </div>
                <p className="text-sm text-white/90">
                  {t("quality_modal.premium_section.feature_2")}
                </p>
              </div>
              <div className="flex items-start gap-2">
                <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="w-3 h-3" />
                </div>
                <p className="text-sm text-white/90">
                  {t("quality_modal.premium_section.feature_3")}
                </p>
              </div>
            </div>
          </div>

          {hasEnoughCredits ? (
            <Button
              size="3"
              className="w-full !bg-white !text-indigo-600 hover:!bg-white/90 font-semibold mt-6"
              onClick={() => {
                onAddMorePictures();
                onClose();
              }}
            >
              {t("quality_modal.premium_section.button_add_pictures")}
            </Button>
          ) : (
            <div className="mt-6 space-y-3">
              <div className="flex flex-col space-y-2 sm:space-y-0 sm:flex-row items-center justify-between w-full">
                {/* Payment Type Switch */}
                <div className="flex items-center justify-center bg-white/20 backdrop-blur-sm p-1 rounded-full sm:rounded-r-none w-full sm:w-max">
                  <button
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      paymentType === "subscribe"
                        ? "bg-white text-indigo-600 shadow-sm"
                        : "text-white hover:text-white/80"
                    }`}
                    onClick={() => setPaymentType("subscribe")}
                  >
                    {t("quality_modal.payment_type.subscribe", "Subscribe")}
                  </button>
                  <button
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      paymentType === "once"
                        ? "bg-white text-indigo-600 shadow-sm"
                        : "text-white hover:text-white/80"
                    }`}
                    onClick={() => setPaymentType("once")}
                  >
                    {t("quality_modal.payment_type.pay_once", "Pay Once")}
                  </button>
                </div>

                {/* Direct Checkout Button */}
                <Button
                  size="3"
                  className="w-full sm:w-auto sm:grow !h-11 !bg-white !text-indigo-600 hover:!bg-white/90 font-semibold sm:rounded-l-none"
                  onClick={handleDirectCheckout}
                >
                  {paymentType === "subscribe"
                    ? t(
                        "quality_modal.premium_section.button_subscribe",
                        "Subscribe Now",
                      )
                    : t(
                        "quality_modal.premium_section.button_buy_credits",
                        "Buy Credits",
                      )}
                </Button>
              </div>

              {/* Pricing Info */}
              <div className="text-center text-white/90 text-sm">
                {paymentType === "subscribe" ? (
                  <span>
                    {t(
                      "quality_modal.pricing.subscribe",
                      "$19/month • 2,000 credits/month",
                    )}
                  </span>
                ) : (
                  <span>
                    {t(
                      "quality_modal.pricing.pay_once",
                      "$24 • 3,000 credits one-time, never expires",
                    )}
                  </span>
                )}
              </div>

              {/* View All Plans Link */}
              <button
                onClick={handleViewAllPlans}
                className="w-full text-center text-white/80 hover:text-white text-sm underline"
              >
                {t("quality_modal.view_all_plans", "See all plans and options")}
              </button>
            </div>
          )}
        </div>
      </div>
    </ResponsiveModal>
  );
};
